import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { env } from '../config/env'
import { useAuth } from './useAuth'

const CONTACT = 'site-inspection-app@prashamhtrivedi.in'
const MAILTO = `mailto:${CONTACT}?subject=Onboard%20our%20crew%20to%20Site%20Inspection`

export function DemoLanding() {
  const { user, verifyState, errorMessage, signIn } = useAuth()
  const [pendingRole, setPendingRole] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (user && verifyState === 'verified') navigate('/projects', { replace: true })
  }, [user, verifyState, navigate])

  const onPickRole = async (
    roleEmail: string,
    rolePassword: string,
    role: string,
  ) => {
    if (!rolePassword || pendingRole) return
    setPendingRole(role)
    try {
      await signIn(roleEmail, rolePassword)
    } catch {
      // surfaced via errorMessage
    } finally {
      setPendingRole(null)
    }
  }

  const isBusy = pendingRole !== null || verifyState === 'verifying'

  return (
    <div className="min-h-screen bg-blueprint relative overflow-hidden">
      <div className="hazard-tape h-2.5 sm:h-3 w-full" aria-hidden />

      <svg
        className="pointer-events-none absolute -top-40 -right-40 w-[800px] h-[800px] text-blueprint-500/20 hidden md:block"
        viewBox="0 0 800 800"
        fill="none"
        aria-hidden
      >
        <circle cx="400" cy="400" r="380" stroke="currentColor" strokeWidth="1" strokeDasharray="4 8" />
        <circle cx="400" cy="400" r="280" stroke="currentColor" strokeWidth="1" />
        <circle cx="400" cy="400" r="180" stroke="currentColor" strokeWidth="1" strokeDasharray="2 6" />
        <line x1="20" y1="400" x2="780" y2="400" stroke="currentColor" strokeWidth="0.5" />
        <line x1="400" y1="20" x2="400" y2="780" stroke="currentColor" strokeWidth="0.5" />
      </svg>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-10 pb-12 sm:pb-20">
        {/* Header strip */}
        <div className="flex items-center justify-between mb-8 sm:mb-12 reveal-rise">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-11 sm:h-11 grid place-items-center bg-accent-500 text-surface-950 font-display font-bold text-base sm:text-lg leading-none">
              SI
            </div>
            <div className="leading-tight">
              <div className="font-display text-base sm:text-lg text-surface-50">Site Inspection</div>
              <div className="text-[12px] sm:text-[13px] text-surface-400">For builders</div>
            </div>
          </div>
          <a
            href={MAILTO}
            className="inline-flex items-center gap-2 text-[13px] sm:text-sm text-surface-100 hover:text-accent-400 transition shrink-0"
          >
            <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse" aria-hidden />
            <span className="hidden xs:inline sm:inline">Talk to us</span>
            <span className="xs:hidden sm:hidden">Contact</span>
          </a>
        </div>

        {/* HERO + role cards */}
        <section className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-start mb-14 sm:mb-20">
          {/* LEFT copy */}
          <div className="lg:col-span-6 relative">
            <div
              className="flex items-center gap-3 mb-5 reveal-rise"
              style={{ animationDelay: '0.05s' }}
            >
              <span className="h-px w-8 sm:w-10 bg-accent-500" />
              <span className="text-[13px] sm:text-sm text-accent-400">
                For builders in Gujarat
              </span>
            </div>

            <h1
              className="font-display text-[clamp(2.5rem,11vw,7rem)] leading-[0.88] text-surface-50 reveal-rise"
              style={{ animationDelay: '0.1s', fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'wght' 500" }}
            >
              Built on
              <br />
              <span
                className="italic"
                style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'wght' 400" }}
              >
                site,
              </span>{' '}
              <span className="text-accent-500">measured</span>
              <br />
              by you.
            </h1>

            <p
              className="mt-6 sm:mt-8 max-w-xl text-surface-100/85 text-base sm:text-lg leading-relaxed reveal-rise"
              style={{ animationDelay: '0.2s' }}
            >
              A simple walk-through of the app your foreman, store-keeper and
              site engineer will use every day. Pick a role on the right,
              one tap signs you straight in.
            </p>

            <div
              className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-xl reveal-rise"
              style={{ animationDelay: '0.28s' }}
            >
              {['Track every site', 'Material & labour', 'Daily reports'].map((it) => (
                <div key={it} className="border-l-2 border-accent-500/60 pl-3 py-1">
                  <div className="text-surface-50 text-[15px] sm:text-base font-medium">
                    {it}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="hidden lg:block absolute -top-4 right-0 reveal-drop"
              style={{ animationDelay: '0.5s' }}
              aria-hidden
            >
              <div className="stamp text-rebar-500 text-xs">
                <div>Sample data</div>
                <div className="text-[10px] opacity-80 mt-0.5">DEMO · {new Date().getFullYear()}</div>
              </div>
            </div>
          </div>

          {/* RIGHT role cards (click-to-sign-in) */}
          <aside className="lg:col-span-6 reveal-rise" style={{ animationDelay: '0.34s' }}>
            <div
              id="role-picker"
              className="bracket-corners relative bg-surface-900/90 backdrop-blur-sm border border-surface-700 p-5 sm:p-7"
            >
              <div className="mb-5 pb-4 border-b border-surface-700">
                <h2 className="font-display text-2xl sm:text-3xl text-surface-50 leading-none">
                  Pick a role to sign in
                </h2>
                <p className="text-[13px] sm:text-sm text-surface-400 mt-2">
                  These are the only accounts available in the demo. Tap one to
                  log in instantly, no password to type.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {env.demoCreds.map((c) => {
                  const disabled = !c.password || isBusy
                  const isPending = pendingRole === c.role
                  return (
                    <button
                      key={c.role}
                      type="button"
                      disabled={disabled}
                      onClick={() => onPickRole(c.email, c.password, c.role)}
                      className={`group text-left relative bg-surface-900 border transition-all duration-200 ${
                        isPending
                          ? 'border-accent-500 -translate-y-0.5'
                          : 'border-surface-700 active:border-accent-500 sm:hover:border-accent-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      style={{
                        boxShadow: isPending ? '4px 4px 0 0 var(--color-accent-500)' : undefined,
                      }}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-display text-lg sm:text-xl text-surface-50 leading-tight">
                            {c.role}
                          </div>
                          <span
                            className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                              isPending
                                ? 'bg-accent-500 animate-pulse'
                                : 'bg-surface-700 group-hover:bg-accent-500'
                            }`}
                            aria-hidden
                          />
                        </div>
                        <div className="mt-3 font-mono text-[11px] sm:text-[12px]">
                          <div className="flex gap-2 break-all">
                            <span className="text-surface-400 shrink-0 w-10">Email</span>
                            <span className="text-surface-100 break-all">{c.email}</span>
                          </div>
                        </div>
                        <div
                          className={`mt-3 text-[13px] transition ${
                            isPending
                              ? 'text-accent-400'
                              : 'text-surface-400 group-hover:text-accent-400'
                          }`}
                        >
                          {isPending
                            ? 'Signing in…'
                            : !c.password
                              ? 'Unavailable'
                              : 'Tap to sign in →'}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              {env.demoCreds.length === 0 && (
                <p className="mt-4 text-sm text-rebar-500">
                  No demo roles configured. Operator must set
                  <code className="ml-1 font-mono text-xs text-surface-100 bg-surface-950 px-2 py-1 border border-surface-700">
                    VITE_DEMO_CREDS_JSON
                  </code>
                  .
                </p>
              )}

              {verifyState === 'unprovisioned' && (
                <p className="mt-4 text-sm text-accent-400 leading-relaxed">
                  The demo isn't ready yet. Operator action required:
                  <code className="block mt-1 font-mono text-xs text-surface-100 bg-surface-950 px-2 py-1.5 border border-surface-700 break-all">
                    POST /api/admin/demo/reset
                  </code>
                </p>
              )}
              {errorMessage && (
                <p className="mt-4 border-l-2 border-rebar-500 bg-rebar-500/5 px-3 py-2 text-sm text-rebar-500 break-words">
                  {errorMessage}
                </p>
              )}
            </div>

            {/* Mobile-only stamp under everything */}
            <div className="lg:hidden mt-7 flex justify-center" aria-hidden>
              <div className="stamp text-rebar-500 text-xs">
                <div>Sample data</div>
                <div className="text-[10px] opacity-80 mt-0.5">DEMO · {new Date().getFullYear()}</div>
              </div>
            </div>
          </aside>
        </section>

        {/* CTA */}
        <section className="reveal-rise" style={{ animationDelay: '0.5s' }}>
          <div className="relative border-2 border-accent-500 bg-surface-900">
            <div className="hazard-tape-thin h-2 absolute top-0 left-0 right-0" aria-hidden />
            <div className="hazard-tape-thin h-2 absolute bottom-0 left-0 right-0" aria-hidden />
            <div className="px-5 sm:px-10 py-8 sm:py-12">
              <div className="text-[15px] sm:text-base text-accent-400 mb-3">
                Ready to use it for real?
              </div>
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-surface-50 leading-tight">
                Everything here is{' '}
                <span className="italic text-rebar-500">sample data.</span>
                <br className="hidden sm:inline" />{' '}
                Want it for your sites?
              </h3>
              <p className="mt-3 sm:mt-4 text-surface-100/85 max-w-2xl text-base sm:text-lg leading-relaxed">
                <span className="font-semibold text-accent-400 uppercase tracking-wide">Your</span>{' '}
                projects,{' '}
                <span className="font-semibold text-accent-400 uppercase tracking-wide">your</span>{' '}
                vendors,{' '}
                <span className="font-semibold text-accent-400 uppercase tracking-wide">your</span>{' '}
                team,{' '}
                <span className="font-semibold text-accent-400 uppercase tracking-wide">your</span>{' '}
                workflow, set up the way you already run them. Tell us a bit
                about your sites and we will have you running on real data in
                days, not weeks. Phone calls welcome too.
              </p>
            </div>
          </div>
        </section>

        {/* Footer — owns the email CTA */}
        <footer className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-surface-700">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[13px] sm:text-sm text-surface-400">
              <span className="text-surface-100">Site Inspection</span> · demo · beta
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <div className="flex flex-col">
                <span className="text-[11px] uppercase tracking-wide text-surface-400">Email us</span>
                <a
                  href={MAILTO}
                  className="font-mono text-sm sm:text-base text-accent-400 hover:text-surface-50 transition break-all leading-tight"
                >
                  {CONTACT}
                </a>
              </div>
              <a
                href={MAILTO}
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-surface-950 text-sm font-semibold px-4 py-2.5 transition justify-center"
              >
                Get me started <span aria-hidden>→</span>
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
